/** СТАТУСЫ для DOCFLOW-процессов и для Документов участвующих в DOCFLOW-процессах
 * Сами процессы docflow:
 *  docflow_process - тип статуса в БД docflowstatus
 *  docflow_process_route - тип статуса в БД docflowstatus
 *  docflow_process_task - тип статуса в БД docflowstatus
 *
 * Документы подключенные к DOCFLOW-процессам:
 *  act - тип статуса в БД act_doc_state
 *  budget - тип статуса в БД budget_doc_state
 *  contract - тип статуса в БД contract_doc_state
 *  invoice - тип статуса в БД invoice_doc_state
 *  task - тип статуса в БД docflowstatus
 *
 *  ВАЖНО !!! Статус 'new' должен быть у ВСЕХ Документов !!!
 */
import type { Reference, SysShared } from '@platform/entities/entity.class';
import { normalize } from '@platform/shared/helpers/formatters';

/** Тип для получения ключей из имен статусов. пример: NameKey<'ACCOUNT_TYPE'> */
export type NameKey<K extends keyof typeof NAME> = keyof (typeof NAME)[K];

// имена статусов по умолчанию
export type PlainStatus = 'new' | 'started' | 'approved' | 'not_approved' | 'canceled' | 'action';
export type DocflowStatus = PlainStatus | { value: PlainStatus };
export type ExtendedDocflowStatus = {
    label: string;
    name: string;
    action: string;
    value: PlainStatus | string;
    icon: string;
    iconColor: string;
    sort: number;
    fillGaugeValue?: number; // % заполнения ячейки от 0 до 100 (слева направо)
    fillGaugeColor?: string; // цвет заполнения ячейки
    allowedTransitions?: string[];
    doubleCellStyle?: {
        textColor: string;
        label: string;
        icon: string;
    };
};

const DEFAULT_NAME = {
    new: {
        label: 'Новый',
        value: 'new',
        name: 'Новый',
        action: 'Новая',
        icon: 'fas fa-circle',
        iconColor: '#55b1f6',
        sort: 1,
        doubleCellStyle: {
            icon: 'o_edit',
            textColor: 'blue',
            label: 'Новый',
        },
    },
    started: {
        label: 'Согласование',
        value: 'started',
        name: 'Согласование',
        action: 'Запустить',
        icon: 'fas fa-dot-circle',
        iconColor: '#ffa500',
        sort: 2,
        doubleCellStyle: {
            icon: 'flaky',
            textColor: 'yellow',
            label: 'На согласовании',
        },
    },
    approved: {
        label: 'Согласован',
        value: 'approved',
        name: 'Согласован',
        action: 'Согласовать',
        icon: 'fas fa-check-circle',
        iconColor: '#4caf50',
        sort: 4,
        doubleCellStyle: {
            icon: 'o_check_circle',
            textColor: 'green',
            label: 'Согласован',
        },
    },
    not_approved: {
        label: 'Отклонен',
        value: 'not_approved',
        name: 'Отклонен',
        action: 'Отклонить',
        icon: 'fas fa-times-circle',
        iconColor: '#ff5454',
        sort: 5,
        doubleCellStyle: {
            icon: 'o_cancel',
            textColor: 'red',
            label: 'Отклонен',
        },
    },
    canceled: {
        label: 'Отменен',
        value: 'canceled',
        name: 'Отменен',
        action: 'Отменить',
        icon: 'fas fa-ban',
        iconColor: '#aaaaaa',
        sort: 6,
        doubleCellStyle: {
            icon: 'o_block',
            textColor: 'gray',
            label: 'Отклонен',
        },
    },
} as Record<PlainStatus, ExtendedDocflowStatus>;

// options для выбора в списках по умолчанию
const DEFAULT_OPTIONS = [
    DEFAULT_NAME.new,
    DEFAULT_NAME.started,
    DEFAULT_NAME.approved,
    DEFAULT_NAME.not_approved,
    DEFAULT_NAME.canceled,
];

// имена статусов
export const NAME = {
    DOCFLOW: DEFAULT_NAME as Record<string, ExtendedDocflowStatus>,
    ACT: DEFAULT_NAME,
    ACT_KS: DEFAULT_NAME,
    BUDGET: DEFAULT_NAME,
    CONTRACT: {
        new: {
            label: 'Новый',
            value: 'new',
            name: 'Новый',
            action: 'Новая',
            icon: 'fas fa-circle',
            iconColor: '#55b1f6',
            sort: 1,
        },
        started: {
            label: 'Согласование',
            value: 'started',
            name: 'Согласование',
            action: 'Запустить',
            icon: 'fas fa-dot-circle',
            iconColor: '#ffa500',
            sort: 2,
        },
        approved: {
            label: 'Согласован',
            value: 'approved',
            name: 'Согласован',
            action: 'Согласовать',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            sort: 4,
        },
        not_approved: {
            label: 'Отклонен',
            value: 'not_approved',
            name: 'Отклонен',
            action: 'Отклонить',
            icon: 'fas fa-times-circle',
            iconColor: '#ff5454',
            sort: 5,
        },
        canceled: {
            label: 'Отменен',
            value: 'canceled',
            name: 'Отменен',
            action: 'Отменить',
            icon: 'fas fa-ban',
            iconColor: '#aaaaaa',
            sort: 6,
        },
        closed: {
            label: 'Закрыт',
            value: 'closed',
            name: 'Закрыт',
            action: 'Закрыть',
            icon: 'fas fa-check-circle',
            iconColor: '#aaaaaa',
            sort: 7,
        },
    },
    INVOICE: DEFAULT_NAME,
    WAREHOUSE_DOC: {
        ...DEFAULT_NAME,
        started: {
            ...DEFAULT_NAME.started,
            action: 'Согласование',
        },
    },
    WAREHOUSE_INVENTORY: {
        ...DEFAULT_NAME,
        started: {
            ...DEFAULT_NAME.started,
            action: 'Согласование',
        },
    },
    WORK_VOLUME: DEFAULT_NAME,
    ESTIMATE_RD: DEFAULT_NAME,
    TASK: {
        new: {
            label: 'Новая',
            value: 'new',
            name: 'Новая',
            action: 'Новая',
            icon: 'fas fa-circle',
            iconColor: '#55b1f6',
            sort: 1,
        },
        started: {
            label: 'В работе',
            value: 'started',
            name: 'В работе',
            action: 'В работу',
            icon: 'fas fa-dot-circle',
            iconColor: '#ffa500',
            sort: 2,
        },
        action: {
            label: 'Создание документа',
            value: 'action',
            name: 'Создание документа',
            action: 'Создать документ',
            icon: 'fas fa-plus-circle',
            iconColor: '#ffa500',
            sort: 3,
        },
        approved: {
            label: 'Исполнена',
            value: 'approved',
            name: 'Исполнена',
            action: 'Исполнить (согласовать)',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            sort: 4,
        },
        not_approved: {
            label: 'Отклонена',
            value: 'not_approved',
            name: 'Отклонена',
            action: 'Отклонить',
            icon: 'fas fa-times-circle',
            iconColor: '#ff5454',
            sort: 5,
        },
        canceled: {
            label: 'Отменена',
            value: 'canceled',
            name: 'Отменена',
            action: 'Отменить',
            icon: 'fas fa-ban',
            iconColor: '#cdcdcd',
            sort: 6,
        },
    } as Record<PlainStatus, ExtendedDocflowStatus>,
    REQUEST_PRICE_STATE: {
        new: {
            label: 'Новое КП',
            value: 'new',
            name: 'Новое КП',
            action: 'Новая',
            icon: 'fas fa-circle',
            iconColor: '#55b1f6',
            sort: 1,
        },
        shared: {
            label: 'Отправлено контрагенту',
            value: 'shared',
            name: 'Отправлено контрагенту',
            action: 'Отправить контрагенту',
            icon: 'fas fa-dot-circle',
            iconColor: '#ffa500',
            sort: 2,
        },
        filled: {
            label: 'КП отправлено на рассмотрение заказчику',
            value: 'filled',
            name: 'Заполнено контрагентом',
            action: 'Заполненить контрагентом',
            icon: 'fas fa-plus-circle',
            iconColor: '#ffa500',
            sort: 3,
        },
        approved: {
            label: 'КП принято заказчиком к рассмотрению',
            value: 'approved',
            name: 'Принято заказчиком',
            action: 'Принять заказчиком',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            sort: 4,
        },
        not_approved: {
            label: 'КП отклонено заказчиком',
            value: 'not_approved',
            name: 'Отклонено',
            action: 'Отклонить',
            icon: 'fas fa-times-circle',
            iconColor: '#ff5454',
            sort: 5,
        },
        canceled: {
            label: 'КП отменено заказчиком',
            value: 'canceled',
            name: 'Отменено',
            action: 'Отменить',
            icon: 'fas fa-ban',
            iconColor: '#cdcdcd',
            sort: 6,
        },
    },
    REQUEST_DOC_STATE: {
        new: {
            label: 'Новая',
            value: 'new',
            name: 'Новая',
            action: 'Новая',
            icon: 'fas fa-circle',
            iconColor: '#55b1f6',
            sort: 1,
        },
        started: {
            label: 'Согласование 1 этап',
            value: 'started',
            name: 'Согласование 1 этап',
            action: 'Согласование 1 этап',
            icon: 'fas fa-pause-circle',
            iconColor: '#ffa500',
            sort: 2,
        },
        startedUKS: {
            label: 'Согласование 2 этап',
            value: 'startedUKS',
            name: 'Согласование 2 этап',
            action: 'Согласование 2 этап',
            icon: 'fas fa-pause-circle',
            iconColor: '#ffa500',
            sort: 3,
        },
        started_3: {
            label: 'Согласование 3 этап',
            value: 'started_3',
            name: 'Согласование 3 этап',
            action: 'Согласование 3 этап',
            icon: 'fas fa-pause-circle',
            iconColor: '#ffa500',
            sort: 3,
        },
        approved: {
            label: 'В работе',
            value: 'approved',
            name: 'В работе',
            action: 'В работе',
            icon: 'fas fa-play-circle',
            iconColor: '#ffa500',
            sort: 4,
        },
        executed: {
            label: 'Исполнена',
            value: 'executed',
            name: 'Исполнена',
            action: 'Исполнена',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            sort: 5,
        },
        closed: {
            label: 'Закрыта',
            value: 'closed',
            name: 'Закрыта',
            action: 'Закрыта',
            icon: 'fas fa-check-circle',
            iconColor: '#cdcdcd',
            sort: 6,
        },
        not_approved: {
            label: 'Отклонена',
            value: 'not_approved',
            name: 'Отклонена',
            action: 'Отклонена',
            icon: 'fas fa-times-circle',
            iconColor: '#ff5454',
            sort: 7,
        },
        canceled: {
            label: 'Отменена',
            value: 'canceled',
            name: 'Отменена',
            action: 'Отменена',
            icon: 'fas fa-ban',
            iconColor: '#cdcdcd',
            sort: 8,
        },
    },
    COMMERCIAL_OFFER_STATE: {
        new: {
            label: 'Черновик',
            value: 'new',
            name: 'Черновик',
            action: 'Черновик',
            icon: 'fas fa-circle',
            iconColor: '#cdcdcd',
            allowedTransitions: ['started', 'action', 'canceled'],
            sort: 1,
        },
        started: {
            label: 'Отправлен запрос',
            value: 'started',
            name: 'Отправлен запрос',
            action: 'В работу',
            icon: 'fas fa-dot-circle',
            iconColor: '#55b1f6',
            allowedTransitions: ['action', 'canceled'],
            sort: 2,
        },
        action: {
            label: 'КП получено',
            value: 'action',
            name: 'КП получено',
            action: 'КП получено',
            icon: 'fas fa-plus-circle',
            iconColor: '#ffa500',
            allowedTransitions: ['canceled'],
            sort: 3,
        },
        approved: {
            label: 'КП рассмотрено',
            value: 'approved',
            name: 'КП рассмотрено',
            action: 'КП рассмотрено',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            allowedTransitions: [],
            sort: 4,
        },
        canceled: {
            label: 'Отменен',
            value: 'canceled',
            name: 'Отменен',
            action: 'Отменен',
            icon: 'fas fa-ban',
            iconColor: '#cdcdcd',
            allowedTransitions: [],
            sort: 5,
        },
        invoiceCreated: {
            label: 'Создан счет',
            value: 'invoiceCreated',
            name: 'Создан счёт',
            action: 'Создан счёт',
            icon: 'fas fa-plus',
            iconColor: '#4caf50',
            allowedTransitions: [],
            sort: 6,
        },
        invoiceRejected: {
            label: 'Счет отклонен',
            value: 'invoiceRejected',
            name: 'Счет отклонен',
            action: 'Счет отклонен',
            icon: 'fas fa-times-circle',
            iconColor: '#ff5454',
            allowedTransitions: [],
            sort: 6,
        },
    },
    COMMERCIAL_OFFER_GROUP_STATE: {
        action: {
            label: 'В работе',
            value: 'action',
            name: 'В работе',
            action: 'В работе',
            icon: 'fas fa-plus-circle',
            iconColor: '#ffa500',
            allowedTransitions: ['canceled', 'closed'],
            sort: 1,
        },
        canceled: {
            label: 'Отменен',
            value: 'canceled',
            name: 'Отменен',
            action: 'Отменен',
            icon: 'fas fa-ban',
            iconColor: '#cdcdcd',
            allowedTransitions: [],
            sort: 2,
        },
        closed: {
            label: 'Закрыт',
            value: 'closed',
            name: 'Закрыт',
            action: 'Закрыть',
            icon: 'fas fa-check-circle',
            iconColor: '#aaaaaa',
            allowedTransitions: [],
            sort: 3,
        },
    },
    VERSION_PD: {
        new_pd: {
            label: 'Черновик',
            value: 'new_pd',
            name: 'Черновик',
            action: 'Черновик',
            icon: 'fas fa-circle',
            iconColor: '#55b1f6',
            sort: 1,
        },
        started_pd: {
            label: 'Экспертиза пройдена',
            value: 'started_pd',
            name: 'Экспертиза пройдена',
            action: 'Экспертиза пройдена',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            sort: 2,
        },
        approved_pd: {
            label: 'Тех. комиссия пройдена',
            value: 'approved_pd',
            name: 'Тех. комиссия пройдена',
            action: 'Тех. комиссия пройдена',
            icon: 'fas fa-circle',
            iconColor: '#4caf50',
            sort: 3,
        },
        not_approved_pd: {
            label: 'Аннулирован',
            value: 'not_approved_pd',
            name: 'Аннулирован',
            action: 'Аннулирован',
            icon: 'fas fa-ban',
            iconColor: '#aaaaaa',
            sort: 6,
        },
    },
    VERSION_RD: {
        new_rd: {
            label: 'Черновик',
            value: 'new_rd',
            name: 'Черновик',
            action: 'Черновик',
            icon: 'fas fa-circle',
            iconColor: '#55b1f6',
            sort: 1,
        },
        started_rd: {
            label: 'Сигнальный вариант',
            value: 'started_rd',
            name: 'Сигнальный вариант',
            action: 'Сигнальный вариант',
            icon: 'fas fa-arrows-rotate',
            iconColor: '#ffa500',
            sort: 2,
        },
        approved_rd: {
            label: 'Выдано в производство работ',
            value: 'approved_rd',
            name: 'Выдано в производство работ',
            action: 'Выдано в производство работ',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            sort: 3,
        },
        not_approved_rd: {
            label: 'Аннулирован',
            value: 'not_approved_rd',
            name: 'Аннулирован',
            action: 'Аннулирован',
            icon: 'fas fa-ban',
            iconColor: '#aaaaaa',
            sort: 6,
        },
    },
    GIS_ORG: {
        new: {
            label: 'Новая',
            value: 'new',
            name: 'Новая',
            action: 'Новая',
            icon: 'fas fa-circle',
            iconColor: '#55b1f6',
            sort: 1,
        },
        approved: {
            label: 'Согласована',
            value: 'approved',
            name: 'Согласована',
            action: 'Согласовать',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            sort: 4,
        },
        not_approved: {
            label: 'Не согласована',
            value: 'not_approved',
            name: 'Не согласована',
            action: 'Не согласовать',
            icon: 'fas fa-times-circle',
            iconColor: '#ff5454',
            sort: 5,
        },
        canceled: {
            label: 'Отменена',
            value: 'canceled',
            name: 'Отменена',
            action: 'Отменить',
            icon: 'fas fa-ban',
            iconColor: '#aaaaaa',
            sort: 6,
        },
    },
    GIS_DEBT_REQUEST: {
        new: {
            label: 'Новая',
            value: 'new',
            name: 'Новая',
            action: 'Новая',
            icon: 'fas fa-circle',
            iconColor: '#55b1f6',
            sort: 1,
        },
        started: {
            label: 'В обработке',
            value: 'started',
            name: 'В обработке',
            action: 'Обработать',
            icon: 'fas fa-play-circle',
            iconColor: '#ffa500',
            sort: 3,
        },
        approved: {
            label: 'Согласована',
            value: 'approved',
            name: 'Согласована',
            action: 'Согласовать',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            sort: 4,
        },
        not_approved: {
            label: 'Не согласована',
            value: 'not_approved',
            name: 'Не согласована',
            action: 'Не согласовать',
            icon: 'fas fa-times-circle',
            iconColor: '#ff5454',
            sort: 5,
        },
        canceled: {
            label: 'Отменена',
            value: 'canceled',
            name: 'Отменена',
            action: 'Отменить',
            icon: 'fas fa-ban',
            iconColor: '#aaaaaa',
            sort: 6,
        },
    },
    PROJECT: {
        new: {
            label: 'Новая',
            name: 'Новая',
            value: 'new',
            action: 'Новая',
            icon: 'fas fa-circle',
            iconColor: '#55b1f6',
            // fillGaugeValue: 10,
            // fillGaugeColor: '#ffe9a4',
            sort: 1,
        },
        approved: {
            label: 'В работе',
            value: 'approved',
            name: 'В работе',
            action: 'В работу',
            icon: 'fas fa-check-circle',
            iconColor: '#4caf50',
            // fillGaugeValue: 60,
            // fillGaugeColor: '#c8ffc8',
            sort: 4,
        },
        canceled: {
            label: 'Отменена',
            value: 'canceled',
            name: 'Отменена',
            action: 'Отменить',
            icon: 'fas fa-ban',
            iconColor: '#aaaaaa',
            // fillGaugeValue: 100,
            // fillGaugeColor: '#cdcdcd',
            sort: 6,
        },
        closed: {
            label: 'Закрыт',
            value: 'closed',
            name: 'Закрыт',
            action: 'Закрыть',
            icon: 'fas fa-check-circle',
            iconColor: '#cdcdcd',
            // fillGaugeValue: 100,
            // fillGaugeColor: '#cdcdcd',
            sort: 7,
        },
    } as Record<string, ExtendedDocflowStatus>,
    ACCOUNT_TYPE: {
        // все допустимые типы расчётных счетов
        // @ts-ignore
        normal: {
            label: 'Обычный',
            name: 'Обычный',
            value: 'normal',
        },
        // @ts-ignore
        control: {
            label: 'Контролируемый',
            name: 'Контролируемый',
            value: 'control',
        },
        // @ts-ignore
        treasury: {
            label: 'Казначейский',
            name: 'Казначейский',
            value: 'treasury',
        },
        // @ts-ignore
        cash: {
            label: 'Наличные',
            name: 'Наличные',
            value: 'cash',
        },
    },
    ACCOUNT_TYPE_INVOICE: {
        // типы расчётных счетов для использования в реестрах на оплату и в счетах
        // @ts-ignore
        normal: {
            label: 'Обычный',
            name: 'Обычный',
            value: 'normal',
        },
        // @ts-ignore
        control: {
            label: 'Контролируемый',
            name: 'Контролируемый',
            value: 'control',
        },
        // @ts-ignore
        treasury: {
            label: 'Казначейский',
            name: 'Казначейский',
            value: 'treasury',
        },
    },
    PAYMENT_REGISTER: Object.fromEntries(
        ['new', 'approved', 'canceled'].map((name) => [name, DEFAULT_NAME[name as PlainStatus]])
    ),
} satisfies Record<string, Record<string, ExtendedDocflowStatus>>;

// options для выбора в списках
export const OPTIONS = {
    DOCFLOW: DEFAULT_OPTIONS,
    ACT: DEFAULT_OPTIONS,
    BUDGET: DEFAULT_OPTIONS,
    CONTRACT: [
        NAME.CONTRACT.new,
        NAME.CONTRACT.started,
        NAME.CONTRACT.approved,
        NAME.CONTRACT.canceled,
        NAME.CONTRACT.not_approved,
        NAME.CONTRACT.closed,
    ],
    INVOICE: DEFAULT_OPTIONS,
    WORK_VOLUME: DEFAULT_OPTIONS,
    TASK: [
        NAME.TASK.new,
        NAME.TASK.started,
        /*NAME.TASK.action,*/ NAME.TASK.approved,
        NAME.TASK.not_approved,
        NAME.TASK.canceled,
    ],
    REQUEST_PRICE_STATE: NAME.REQUEST_PRICE_STATE,
    REQUEST_DOC_STATE: NAME.REQUEST_DOC_STATE,
    COMMERCIAL_OFFER_STATE: [
        NAME.COMMERCIAL_OFFER_STATE.new,
        NAME.COMMERCIAL_OFFER_STATE.started,
        NAME.COMMERCIAL_OFFER_STATE.action,
        NAME.COMMERCIAL_OFFER_STATE.approved,
        NAME.COMMERCIAL_OFFER_STATE.canceled,
        NAME.COMMERCIAL_OFFER_STATE.invoiceCreated,
        NAME.COMMERCIAL_OFFER_STATE.invoiceRejected,
    ],
    COMMERCIAL_OFFER_GROUP_STATE: [
        NAME.COMMERCIAL_OFFER_GROUP_STATE.action,
        NAME.COMMERCIAL_OFFER_GROUP_STATE.closed,
        NAME.COMMERCIAL_OFFER_GROUP_STATE.canceled,
    ],
    GIS_ORG: NAME.GIS_ORG,
    GIS_DEBT_REQUEST: NAME.GIS_DEBT_REQUEST,
    PROJECT: NAME.PROJECT,
};

// options для выбора в списках
export const isReadOnly = {
    WAREHOUSE_DOC: funcIsReadOnly,
    WAREHOUSE_INVENTORY: funcIsReadOnly,
    WORK_VOLUME: funcIsReadOnly,
    DOCFLOW: funcIsReadOnly,
    ACT: funcIsReadOnly,
    ACT_KS: funcIsReadOnly,
    BUDGET: funcIsReadOnly,
    CONTRACT: funcIsReadOnly,
    INVOICE: funcIsReadOnly,
    TASK: funcIsReadOnly,
    ESTIMATE_RD: funcIsReadOnly,
    GIS_ORG: funcIsReadOnly,
    GIS_DEBT_REQUEST: funcIsReadOnly,
    PROJECT: funcIsReadOnly,
};

export const isChildStatuses = {
    WORK_VOLUME: funcIsChildStatuses,
    DOCFLOW: funcIsChildStatuses,
    ACT: funcIsChildStatuses,
    BUDGET: funcIsChildStatuses,
    CONTRACT: funcIsChildStatuses,
    INVOICE: funcIsChildStatuses,
    TASK: funcIsChildStatuses,
    GIS_ORG: funcIsChildStatuses,
    GIS_DEBT_REQUEST: funcIsChildStatuses,
    PROJECT: funcIsChildStatuses,
};

export function funcIsChildStatuses(params: { sys_shared?: SysShared | undefined } | null | undefined) {
    if (params === null || typeof params !== 'object') return false;

    if (typeof params.sys_shared === 'string') return false;

    if (
        typeof params.sys_shared === 'object' &&
        typeof params.sys_shared?.is_child === 'boolean' &&
        params.sys_shared?.is_child
    )
        return true;

    if (
        typeof params.sys_shared === 'object' &&
        typeof params.sys_shared?.is_child === 'string' &&
        params.sys_shared?.is_child === 't'
    )
        return true;

    return false;
}

export function funcIsReadOnly(params: { sys_doc_state?: Reference; sys_shared?: SysShared } | null | undefined) {
    if (params === null || typeof params !== 'object') return true;

    if (typeof params.sys_shared === 'string') return true;

    if (
        typeof params.sys_shared === 'object' &&
        typeof params.sys_shared?.is_child === 'boolean' &&
        params.sys_shared?.is_child
    )
        return true;

    if (
        typeof params.sys_shared === 'object' &&
        typeof params.sys_shared?.is_child === 'string' &&
        params.sys_shared?.is_child === 't'
    )
        return true;

    const status = normalize(params.sys_doc_state);
    return status && status !== DEFAULT_NAME.new.value && status !== DEFAULT_NAME.not_approved.value;
}

export function funcIsTChildShared(params: { sys_shared?: SysShared } | null | undefined) {
    if (params == null || typeof params !== 'object') return false;

    if (typeof params.sys_shared === 'string') return false;

    if (
        typeof params.sys_shared === 'object' &&
        ((typeof params.sys_shared?.is_child === 'boolean' && params.sys_shared?.is_child) ||
            (typeof params.sys_shared?.is_child === 'string' && params.sys_shared?.is_child === 't'))
    )
        return true;

    return false;
}

export function funcIsFChildShared(params: { sys_shared?: SysShared } | null | undefined) {
    if (params === null || typeof params !== 'object') return false;

    if (typeof params.sys_shared === 'string') return false;

    if (
        typeof params.sys_shared === 'object' &&
        ((typeof params.sys_shared?.is_child === 'boolean' && !params.sys_shared?.is_child) ||
            (typeof params.sys_shared?.is_child === 'string' && params.sys_shared?.is_child === 'f'))
    )
        return true;

    return false;
}

// function funcIsReadOnly(status?: string) {
//     //тут важно чтобы статус 'new' всегда был у всех видов Документов
//     //в fileCellRenderer.vue идет проверка по DOCFLOW, а не по конкретному типу Документа
//     return status && status !== DEFAULT_NAME.new.value && status !== DEFAULT_NAME.not_approved.value;
// }

export function isTerminalStatus(status: PlainStatus | null | undefined): boolean {
    return status
        ? [NAME.TASK.approved.value, NAME.TASK.not_approved.value, NAME.TASK.canceled.value].includes(status)
        : false;
}

export function searchStatus(tableName: string, sys_doc_state: string) {
    const lowerTableName = tableName?.toLowerCase?.();
    if (lowerTableName === 'invoice') {
        return sys_doc_state ? NAME.INVOICE[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'act') {
        return sys_doc_state ? NAME.ACT[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'act_ks') {
        return sys_doc_state ? NAME.ACT_KS[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'work_volume') {
        return sys_doc_state ? NAME.WORK_VOLUME[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'contract') {
        // @ts-ignore
        return sys_doc_state ? NAME.CONTRACT[sys_doc_state] : null;
    }
    if (lowerTableName === 'estimate_rd') {
        return sys_doc_state ? NAME.ESTIMATE_RD[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'request_price') {
        // @ts-ignore
        return sys_doc_state ? NAME.REQUEST_PRICE_STATE[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'commercial_offer') {
        // @ts-ignore
        return sys_doc_state ? NAME.COMMERCIAL_OFFER_STATE[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'commercial_offer_group') {
        // @ts-ignore
        return sys_doc_state ? NAME.COMMERCIAL_OFFER_GROUP_STATE[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'gis_org') {
        // @ts-ignore
        return sys_doc_state ? NAME.GIS_ORG[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'gis_debt_request') {
        // @ts-ignore
        return sys_doc_state ? NAME.GIS_DEBT_REQUEST[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'project') {
        // @ts-ignore
        return sys_doc_state ? NAME.PROJECT[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'project_doc_version_pd') {
        // @ts-ignore
        return sys_doc_state ? NAME.VERSION_PD[sys_doc_state as PlainStatus] : null;
    } else if (lowerTableName === 'project_doc_version_rd') {
        // @ts-ignore
        return sys_doc_state ? NAME.VERSION_RD[sys_doc_state as PlainStatus] : null;
    }
}

export function searchStatusList(tableName: string) {
    tableName = tableName?.toLowerCase?.();
    switch (tableName) {
        case 'budget':
            return NAME.BUDGET;
        case 'invoice':
            return NAME.INVOICE;
        case 'get_invoice':
            return NAME.INVOICE;
        case 'act':
            return NAME.ACT;
        case 'act_ks':
            return NAME.ACT_KS;
        case 'work_volume':
            return NAME.WORK_VOLUME;
        case 'contract':
            return NAME.CONTRACT;
        case 'estimate_rd':
            return NAME.ESTIMATE_RD;
        case 'request_price':
            return NAME.REQUEST_PRICE_STATE;
        case 'commercial_offer':
            return NAME.COMMERCIAL_OFFER_STATE;
        case 'request':
            return NAME.REQUEST_DOC_STATE;
        case 'gis_org':
            return NAME.GIS_ORG;
        case 'gis_debt_request':
            return NAME.GIS_DEBT_REQUEST;
        case 'project':
            return NAME.PROJECT;
        case 'project_doc_version_pd':
            return NAME.VERSION_PD;
        case 'project_doc_version_rd':
            return NAME.VERSION_RD;
        case 'payment_register':
            return NAME.PAYMENT_REGISTER;
        case 'warehouse_doc':
            return NAME.WAREHOUSE_DOC;
        case 'warehouse_inventory':
            return NAME.WAREHOUSE_INVENTORY;
    }
}

export function isStateEqual(state1: { value: string } | string | null, state2: { value: string } | string | null) {
    const value1 = typeof state1 === 'object' ? state1?.value : state1;
    const value2 = typeof state2 === 'object' ? state2?.value : state2;

    return value1 === value2;
}

export function getAllowedTransitions(stateName: any, from: { value: string } | string) {
    from = typeof from === 'object' ? from?.value : from;
    if (!stateName || !from) return;

    return stateName?.[from]?.allowedTransitions;
}

export function checkAllowedTransition(
    stateName: any,
    from: { value: string } | string,
    to: { value: string } | string
) {
    from = typeof from === 'object' ? from?.value : from;
    to = typeof to === 'object' ? to?.value : to;

    if (!from || !to || !stateName) return;

    // @ts-ignore
    const allowedTransitions = stateName?.[from]?.allowedTransitions;
    return allowedTransitions?.includes?.(to);
}
